import { BaseComponent, html, nothing, renderMarkdown } from '../../lib/lit.js';
import { router } from '../../lib/router.js';

/**
 * <community-legal-page> — one published legal document, fetched from d3-auth.
 *
 * The Portal displays these documents; it does not own them. d3-auth owns the
 * consent corpus as data (see its `013_consent_documents.sql`, which names this
 * portal as one of the apps that was about to ship its own copy), and serves the
 * current published version unauthenticated at
 * `GET {authBaseUrl}/api/v1/{tenantSlug}/documents/{key}` — added for this
 * consumer in dice-driven/d3-auth#56.
 *
 * Deliberately NOT via lib/api.js: that client targets the community API and
 * attaches a bearer token. This is a different origin, needs no token, and must
 * work signed-out — these pages are reachable from the signed-out footer.
 *
 * A published (key, version) is immutable in d3-auth, so a wording change
 * arrives as a new version rather than an edit. The version is surfaced so a
 * reader can tell which one they are looking at.
 */
export class CommunityLegalPage extends BaseComponent {
    static properties = {
        docKey: { type: String },
        authBaseUrl: { type: String },
        tenantSlug: { type: String },
        _doc: { state: true },
        _loading: { state: true },
        _error: { state: true },
    };

    constructor() {
        super();
        this.docKey = '';
        this.authBaseUrl = '';
        this.tenantSlug = '';
        /** @type {object|null} */
        this._doc = null;
        this._loading = true;
        this._error = '';
        /** @type {string} the docKey a load has already been started for */
        this._loadedFor = '';
    }

    // No connectedCallback fetch: the three inputs are set by the parent AFTER
    // construction, so a load there always sees them empty. Loading from
    // updated() — which runs once the first batch of properties has landed —
    // is what makes the first render actually fetch anything.
    updated(changed) {
        const inputChanged =
            changed.has('docKey') || changed.has('authBaseUrl') || changed.has('tenantSlug');
        if (inputChanged) this._load();
    }

    async _load() {
        // Still waiting on config. Stay in the loading state rather than
        // rendering an error for something that has not failed yet.
        if (!this.docKey || !this.authBaseUrl || !this.tenantSlug) return;
        if (this._loadedFor === this.docKey) return;
        this._loadedFor = this.docKey;
        this._loading = true;
        this._error = '';
        try {
            const url = `${this.authBaseUrl}/api/v1/${this.tenantSlug}/documents/${this.docKey}`;
            const response = await fetch(url);
            if (response.status === 404) {
                // The key has no published version yet. Say so plainly rather
                // than rendering an empty page that looks like a broken fetch.
                throw new Error('This document has not been published yet.');
            }
            if (!response.ok) {
                throw new Error(`Could not load this document (${response.status}).`);
            }
            this._doc = await response.json();
        } catch (err) {
            this._error = String(err.message ?? err);
        } finally {
            this._loading = false;
        }
    }

    render() {
        return html`
            <div class="container" style="max-width: 46rem;">
                <button class="btn btn-link px-0 mb-2" @click=${() => router.navigate('/')}>
                    <i class="bi bi-arrow-left me-1"></i>Back
                </button>

                <div class="card shadow-sm border-0 mb-4">
                    <div class="card-body p-4">
                        ${this._loading ? this._renderLoading() : this._renderBody()}
                    </div>
                </div>
            </div>
        `;
    }

    _renderLoading() {
        return html`
            <div class="text-center py-4">
                <div class="spinner-border text-secondary" role="status">
                    <span class="visually-hidden">Loading…</span>
                </div>
            </div>
        `;
    }

    _renderBody() {
        if (this._error) {
            return html`<div class="alert alert-warning mb-0" role="alert">${this._error}</div>`;
        }
        const doc = this._doc;
        if (!doc) return nothing;

        return html`
            <h1 class="h4 mb-1">${doc.title}</h1>
            <div class="text-muted small mb-4">Version ${doc.version}</div>
            <div class="legal-body">${renderMarkdown(doc.body)}</div>
        `;
    }
}

customElements.define('community-legal-page', CommunityLegalPage);
